import { useMemo } from 'react';
import { registerQuery, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { useGetHydratedLessonQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService.hooks';
import { GET_HYDRATED_LESSON_QUERY } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService.dfc.new';
import getLang from 'LearningCenterUI/utils/getLang';
import { ACADEMY_CATALOG_SERVICE_URI } from '../../constants/ApiConstants';
import { getCachedThenNew, quickFetchOrGet } from '../../utils/ajaxUtils';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { updateCache } from '../../utils/DataFetchingClientCache';
import { updateCache as updateCacheChirp } from '../../utils/DataFetchingClientCache.new';
import { getLanguagesFromLinkedContent } from '../../utils/languageUtils';
/**
 * @deprecated
 * Use `useGetHydratedLessonQuery` instead
 */
const GET_LESSON = registerQuery({
  fieldName: 'lesson',
  args: ['lessonId', 'language'],
  fetcher({
    lessonId,
    language
  }) {
    return quickFetchOrGet(`${ACADEMY_CATALOG_SERVICE_URI}/lessons/${lessonId}/${language}`);
  }
});
export const GET_ALL_LESSONS = registerQuery({
  fieldName: 'allLessons',
  args: ['language'],
  fetcher({
    language
  }) {
    const lang = language || getLang();
    return getCachedThenNew(`lessons-${lang}`, `${ACADEMY_CATALOG_SERVICE_URI}/lessons/${lang}?fallbackLanguage=en`).then(response => response);
  }
});
export const useAvailableLanguagesByLesson = (lessonId, skip) => {
  const {
    data,
    loading,
    error
  } = useGetHydratedLessonQuery({
    variables: {
      lessonId
    },
    skip
  });
  const lessonLanguages = useMemo(() => {
    var _data$lesson, _data$lesson2;
    return getLanguagesFromLinkedContent(data === null || data === void 0 || (_data$lesson = data.lesson) === null || _data$lesson === void 0 || (_data$lesson = _data$lesson.languageLinkedContent) === null || _data$lesson === void 0 ? void 0 : _data$lesson.languageToId, data === null || data === void 0 || (_data$lesson2 = data.lesson) === null || _data$lesson2 === void 0 ? void 0 : _data$lesson2.language);
  }, [data]);
  return {
    lessonLanguages,
    lessonLanguagesLoading: loading,
    lessonLanguagesError: error
  };
};

/**
 * @deprecated
 * Use `updateLessonCacheChirp` instead
 */
export const updateFullLessonCache = (lessonData, language) => {
  updateCache(GET_LESSON, () => lessonData, {
    language: language === null || language === void 0 ? void 0 : language.toLowerCase(),
    lessonId: lessonData.id
  });
};
export const updateLessonCacheChirp = lessonData => {
  updateCacheChirp(GET_HYDRATED_LESSON_QUERY, () => ({
    lesson: lessonData
  }), {
    lessonId: lessonData.id
  });
};

/**
 * @deprecated
 * Use `updateLessonCacheChirp` for chirp queries
 * updatePartialLessonCache will only work for legacy rest queries
 */
export const updatePartialLessonCache = (lessonData, language = getLang()) => {
  updateCache(GET_ALL_LESSONS, allLessonsCache => {
    if (!allLessonsCache.result) {
      return {
        result: [lessonData],
        timestamp: new Date().getTime()
      };
    }
    const existingLessonIdx = allLessonsCache.result.findIndex(cachedLesson => cachedLesson.id === lessonData.id);
    if (existingLessonIdx > -1) {
      allLessonsCache.result[existingLessonIdx] = lessonData;
    }
    allLessonsCache.result.push(lessonData);
    return allLessonsCache;
  }, {
    language: language.toLowerCase()
  });
};

/**
 * @deprecated
 * Use `useFullLessonChirp` instead
 */
export const useFullLesson = (lessonId, language = getLang(), skip, fetchPolicy) => {
  if (!language || !language.length) language = getLang();
  const {
    loading: lessonLoading,
    data: lesson,
    error: lessonError
  } = useQuery(GET_LESSON, {
    variables: {
      lessonId,
      language: language.toLowerCase()
    },
    skip: skip || lessonId === null || isNaN(lessonId),
    fetchPolicy
  });
  return {
    lesson,
    lessonLoading,
    lessonError
  };
};
export const useFullLessonChirp = ({
  lessonId,
  skip,
  fetchPolicy
}) => {
  const {
    loading: lessonLoading,
    data,
    error: lessonError
  } = useGetHydratedLessonQuery({
    variables: {
      lessonId
    },
    skip: skip || lessonId === null || isNaN(lessonId),
    fetchPolicy
  });
  return {
    lesson: data === null || data === void 0 ? void 0 : data.lesson,
    lessonLoading,
    lessonError
  };
};
export const getFullLesson = variables => dataFetchingClient.queryUnwrapped({
  query: GET_HYDRATED_LESSON_QUERY,
  variables
}).then(response => response.lesson);
export const useLessonContentById = ({
  lessonId,
  contentId,
  language,
  fetchPolicy,
  skip
}) => {
  const {
    lesson
  } = useFullLessonChirp({
    lessonId,
    language,
    skip,
    fetchPolicy
  });
  if (!lesson) {
    return undefined;
  }
  return lesson.content.find(content => content.id === contentId);
};